import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    Center,
    Spinner,
} from '@chakra-ui/react';
import { useRef, useState, useCallback, useMemo } from 'react';
import PartOptionNoteForm, { PartOptionNoteFormHandle } from '../components/PartOptionNoteForm';
import { useIsOverflowing } from '../hooks/useIsOverflowing';
import { useGetPartOptions } from '../hooks/useGetPartOptions';

interface PartOptionNoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedOptionId: string;
    orderId: string;
    orderPartId: string;
    notes?: string;
    estimatingNote?: string;
    listPriceAdjusted: boolean;
    refetch: () => void | Promise<unknown>;
    isEditable: boolean;
    partType?: string;
    isAssembly?: boolean;
    drpJob?: boolean;
}

const PartOptionNoteModal = ({
    isOpen,
    onClose,
    selectedOptionId,
    orderId,
    orderPartId,
    notes = '',
    estimatingNote = '',
    listPriceAdjusted,
    refetch,
    isEditable,
    partType,
    isAssembly = false,
    drpJob = false,
}: PartOptionNoteModalProps) => {
    const formRef = useRef<PartOptionNoteFormHandle>(null);
    const [isPending, setIsPending] = useState(false);
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen);

    const shouldFetchOptions = isOpen && !!selectedOptionId && !isAssembly;
    const { data, isSuccess, isError } = useGetPartOptions(
        orderPartId,
        shouldFetchOptions,
    );

    const resolvedOption = useMemo(() => {
        if (!data?.options) return undefined;
        return data.options.find((option) => option._id === selectedOptionId)
            ?? data.options.find((option) => option.selected);
    }, [data?.options, selectedOptionId]);

    const optionsSettled = !shouldFetchOptions || isSuccess || isError;
    const showForm = optionsSettled;

    const handleSave = useCallback(() => {
        formRef.current?.save();
    }, []);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent maxH="85vh" borderRadius="xl">
                <ModalHeader
                    borderBottomWidth={bodyOverflows ? '1px' : '0'}
                    borderBottomStyle="solid"
                    borderColor="gray.300"
                >{isEditable ? 'Adjust List Price / Add Insurer Note' : 'Insurer Note'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody ref={bodyRef} minH={0}>
                    {!showForm ? (
                        <Center py={10}>
                            <Spinner color="#4051B5" />
                        </Center>
                    ) : (
                        <PartOptionNoteForm
                            ref={formRef}
                            selectedOptionId={selectedOptionId}
                            orderId={orderId}
                            orderPartId={orderPartId}
                            notes={resolvedOption?.optionNote ?? notes}
                            estimatingNote={shouldFetchOptions ? (data?.estimateNote ?? '') : estimatingNote}
                            listPriceAdjusted={listPriceAdjusted}
                            isEditable={isEditable}
                            onClose={onClose}
                            refetch={refetch}
                            hideButtons
                            showHeader={false}
                            onPendingChange={setIsPending}
                            partType={resolvedOption?.type ?? partType}
                            source={resolvedOption?.source}
                            insurerBest={resolvedOption?.insurerBest}
                            drpJob={drpJob}
                        />
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button size="action" variant="outlineGray" mr={3} onClick={onClose}>
                        {isEditable ? 'Cancel' : 'Close'}
                    </Button>
                    {isEditable && (
                        <Button
                            size="action"
                            bg="#0071E3"
                            color="white"
                            _hover={{ bg: "#005BC4" }}
                            onClick={handleSave}
                            isLoading={isPending}
                            loadingText="Applying..."
                            isDisabled={isPending || !selectedOptionId || !showForm}
                        >
                            Apply
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default PartOptionNoteModal;
